import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Receipt, CheckCircle2, AlertTriangle, Edit } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Tax information card, HMRC Reporting Rules for Digital Platforms.
 *
 * Sits on the provider Security tab. Collects everything we need to
 * include the provider in the annual HMRC platform-operator return:
 * legal name, address, DOB, entity type (individual / Ltd company),
 * NI number / UTR / Companies House number / VAT number as applicable.
 *
 * Shows a complete/incomplete badge so the provider sees the gate at
 * a glance. The Provider Dashboard surfaces a separate banner when
 * this card is incomplete, so a returning user can&apos;t miss it.
 */
export default function TaxInfoCard() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    entity_type: 'individual',
    legal_name: '',
    address: { line1: '', line2: '', city: '', postcode: '' },
    date_of_birth: '',
    tax_residence: 'GB',
    ni_number: '',
    utr: '',
    company_number: '',
    vat_number: '',
    self_certified: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/tax/profile/me');
      setProfile(res.data);
      if (res.data?.status !== 'missing') {
        setForm(f => ({
          ...f,
          entity_type: res.data.entity_type || 'individual',
          legal_name: res.data.legal_name || '',
          address: res.data.address || { line1: '', line2: '', city: '', postcode: '' },
          date_of_birth: res.data.date_of_birth || '',
          tax_residence: res.data.tax_residence || 'GB',
          ni_number: res.data.ni_number || '',
          utr: res.data.utr || '',
          company_number: res.data.company_number || '',
          vat_number: res.data.vat_number || '',
        }));
      }
    } catch {
      setProfile({ status: 'missing' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    if (!form.self_certified) {
      toast.error('Please tick the self-certification box to continue');
      return;
    }
    setSaving(true);
    try {
      await api.put('/tax/profile/me', form);
      toast.success('Tax information saved');
      setEditing(false);
      load();
      // Notify the dashboard banner so it disappears without a reload.
      try { window.dispatchEvent(new Event('zuriya:tax-profile-updated')); } catch { /* SSR / restricted contexts */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save tax information');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="card-surface p-6" data-testid="tax-info-card-loading">
        <p className="text-sm text-stone-400">Loading tax information&hellip;</p>
      </div>
    );
  }

  const isComplete = profile?.status === 'complete';
  const isMissing = profile?.status === 'missing';

  if (!editing) {
    return (
      <div className="card-surface p-6" data-testid="tax-info-card">
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-[#D4AF37]" />
            <h3 className="font-heading text-lg font-medium text-[#1A1A1A]">Tax information for HMRC</h3>
          </div>
          {isComplete ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid="tax-status-complete">
              <CheckCircle2 className="h-3 w-3" />Complete
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full" data-testid="tax-status-incomplete">
              <AlertTriangle className="h-3 w-3" />Action needed
            </span>
          )}
        </div>
        <p className="text-xs text-stone-500 mb-4">
          UK marketplaces (including Zuriya) must report your earnings to HMRC under Schedule 23 Finance Act 2022.
          We need your tax identifier on file before we can include you in the annual return.
          {' '}<a href="/trust#trust-hmrc" className="text-[#D4AF37] underline">More on this</a>.
        </p>

        {isMissing ? (
          <Button
            onClick={() => setEditing(true)}
            className="btn-primary gap-1"
            data-testid="tax-add-info-btn"
          >
            Add tax information
          </Button>
        ) : (
          <>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs mb-4">
              <div>
                <dt className="text-stone-500">Entity type</dt>
                <dd className="text-[#1A1A1A] capitalize">{profile.entity_type}</dd>
              </div>
              <div>
                <dt className="text-stone-500">Legal name</dt>
                <dd className="text-[#1A1A1A]">{profile.legal_name}</dd>
              </div>
              <div>
                <dt className="text-stone-500">Date of birth</dt>
                <dd className="text-[#1A1A1A]">{profile.date_of_birth}</dd>
              </div>
              <div>
                <dt className="text-stone-500">Tax residence</dt>
                <dd className="text-[#1A1A1A]">{profile.tax_residence}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-stone-500">Registered address</dt>
                <dd className="text-[#1A1A1A]">
                  {profile.address?.line1}
                  {profile.address?.line2 ? `, ${profile.address.line2}` : ''}
                  , {profile.address?.city}, {profile.address?.postcode}
                </dd>
              </div>
              {profile.ni_number && (
                <div>
                  <dt className="text-stone-500">NI number</dt>
                  <dd className="text-[#1A1A1A] font-mono">{profile.ni_number.slice(0, 4)}&middot;&middot;&middot;&middot;{profile.ni_number.slice(-1)}</dd>
                </div>
              )}
              {profile.utr && (
                <div>
                  <dt className="text-stone-500">UTR</dt>
                  <dd className="text-[#1A1A1A] font-mono">&middot;&middot;&middot;&middot;&middot;&middot;{profile.utr.slice(-4)}</dd>
                </div>
              )}
              {profile.company_number && (
                <div>
                  <dt className="text-stone-500">Companies House no.</dt>
                  <dd className="text-[#1A1A1A] font-mono">{profile.company_number}</dd>
                </div>
              )}
              {profile.vat_number && (
                <div>
                  <dt className="text-stone-500">VAT number</dt>
                  <dd className="text-[#1A1A1A] font-mono">{profile.vat_number}</dd>
                </div>
              )}
            </dl>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(true)}
              className="border-[#E6E4DD] gap-1"
              data-testid="tax-edit-btn"
            >
              <Edit className="h-3.5 w-3.5" />Edit
            </Button>
          </>
        )}
      </div>
    );
  }

  // Editing form
  return (
    <div className="card-surface p-6 space-y-4" data-testid="tax-info-form">
      <div className="flex items-center gap-2">
        <Receipt className="h-5 w-5 text-[#D4AF37]" />
        <h3 className="font-heading text-lg font-medium text-[#1A1A1A]">Tax information for HMRC</h3>
      </div>

      <div>
        <Label className="text-xs">Entity type</Label>
        <div className="flex flex-wrap gap-2 mt-1">
          {['individual', 'company'].map(t => (
            <button
              key={t}
              type="button"
              onClick={() => setForm(f => ({ ...f, entity_type: t }))}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${form.entity_type === t ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]' : 'bg-white text-stone-600 border-[#E6E4DD] hover:border-stone-400'}`}
              data-testid={`tax-entity-${t}`}
            >
              {t === 'individual' ? 'Sole trader' : 'Limited company'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Legal name</Label>
          <Input
            value={form.legal_name}
            onChange={(e) => setForm(f => ({ ...f, legal_name: e.target.value }))}
            placeholder={form.entity_type === 'company' ? 'Registered company name' : 'Your full legal name'}
            className="input-zuriya"
            data-testid="tax-legal-name"
          />
        </div>
        <div>
          <Label className="text-xs">Date of birth</Label>
          <Input
            type="date"
            value={form.date_of_birth}
            onChange={(e) => setForm(f => ({ ...f, date_of_birth: e.target.value }))}
            className="input-zuriya"
            data-testid="tax-dob"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <Label className="text-xs">Registered address, line 1</Label>
          <Input
            value={form.address.line1}
            onChange={(e) => setForm(f => ({ ...f, address: { ...f.address, line1: e.target.value } }))}
            placeholder="e.g. 12 High Street"
            className="input-zuriya"
            data-testid="tax-addr-line1"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Address, line 2 (optional)</Label>
          <Input
            value={form.address.line2}
            onChange={(e) => setForm(f => ({ ...f, address: { ...f.address, line2: e.target.value } }))}
            className="input-zuriya"
            data-testid="tax-addr-line2"
          />
        </div>
        <div>
          <Label className="text-xs">City</Label>
          <Input
            value={form.address.city}
            onChange={(e) => setForm(f => ({ ...f, address: { ...f.address, city: e.target.value } }))}
            className="input-zuriya"
            data-testid="tax-addr-city"
          />
        </div>
        <div>
          <Label className="text-xs">Postcode</Label>
          <Input
            value={form.address.postcode}
            onChange={(e) => setForm(f => ({ ...f, address: { ...f.address, postcode: e.target.value } }))}
            className="input-zuriya"
            data-testid="tax-addr-postcode"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Tax residence (ISO 2-letter)</Label>
          <Input
            value={form.tax_residence}
            onChange={(e) => setForm(f => ({ ...f, tax_residence: e.target.value.toUpperCase() }))}
            maxLength={2}
            className="input-zuriya font-mono uppercase"
            data-testid="tax-residence"
          />
        </div>
      </div>

      {form.entity_type === 'individual' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">National Insurance number</Label>
            <Input
              value={form.ni_number}
              onChange={(e) => setForm(f => ({ ...f, ni_number: e.target.value.toUpperCase() }))}
              placeholder="AB123456C"
              className="input-zuriya font-mono"
              data-testid="tax-ni"
            />
            <p className="text-[10px] text-stone-400 mt-0.5">Or provide your UTR below.</p>
          </div>
          <div>
            <Label className="text-xs">UTR (Unique Taxpayer Reference)</Label>
            <Input
              value={form.utr}
              onChange={(e) => setForm(f => ({ ...f, utr: e.target.value }))}
              placeholder="10 digits"
              className="input-zuriya font-mono"
              maxLength={10}
              data-testid="tax-utr"
            />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Companies House number</Label>
            <Input
              value={form.company_number}
              onChange={(e) => setForm(f => ({ ...f, company_number: e.target.value.toUpperCase() }))}
              placeholder="12345678"
              maxLength={8}
              className="input-zuriya font-mono uppercase"
              data-testid="tax-company-number"
            />
          </div>
          <div>
            <Label className="text-xs">Company UTR</Label>
            <Input
              value={form.utr}
              onChange={(e) => setForm(f => ({ ...f, utr: e.target.value }))}
              placeholder="10 digits"
              maxLength={10}
              className="input-zuriya font-mono"
              data-testid="tax-utr"
            />
          </div>
        </div>
      )}

      <div>
        <Label className="text-xs">VAT number (optional)</Label>
        <Input
          value={form.vat_number}
          onChange={(e) => setForm(f => ({ ...f, vat_number: e.target.value.toUpperCase() }))}
          placeholder="GB123456789"
          className="input-zuriya font-mono uppercase"
          data-testid="tax-vat"
        />
        <p className="text-[10px] text-stone-400 mt-0.5">Only required if you&rsquo;re VAT-registered.</p>
      </div>

      <label className="flex items-start gap-2 text-xs text-stone-600 cursor-pointer">
        <input
          type="checkbox"
          checked={form.self_certified}
          onChange={(e) => setForm(f => ({ ...f, self_certified: e.target.checked }))}
          className="mt-0.5 rounded border-stone-300"
          data-testid="tax-self-cert"
        />
        <span>
          I confirm the information above is true and accurate, and I understand Zuriya is required to report
          this to HMRC under Schedule 23 Finance Act 2022.
        </span>
      </label>

      <div className="flex gap-2 justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => { setEditing(false); load(); }}
          className="border-stone-200"
          data-testid="tax-cancel"
        >
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={saving}
          className="btn-primary"
          data-testid="tax-save"
        >
          {saving ? 'Saving\u2026' : 'Save tax information'}
        </Button>
      </div>
    </div>
  );
}
